import { useApiService } from "@art/shared-ui";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ReactNode, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    Box,
    Card,
    CardActionArea,
    Chip,
    Grid2 as Grid,
    IconButton,
    Link,
    Typography,
} from "@mui/material";
import {
    CheckCircleOutline,
    ChatBubbleOutline,
    Autorenew,
    NorthEast,
    ChevronRight,
    MailOutline,
} from "@mui/icons-material";
import { usePageWrapper } from "../../../hooks";
import { TemplateType, TYPE_LIST } from "./template-types";

const typeIcon: Record<string, ReactNode> = {
    approval: <CheckCircleOutline />,
    feedback: <ChatBubbleOutline />,
    inform: <Autorenew />,
    escalate: <NorthEast />,
};


interface ApiTemplateRow {
    type: TemplateType;
    is_custom?: boolean;
}

function ProjectTemplates() {
    const { projectKey = "" } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const apiService = useApiService();

    const projectName = (location.state as { projectName?: string } | null)?.projectName || projectKey;

    const [customized, setCustomized] = useState<Record<string, boolean>>({});

    useEffect(() => {
        const fetchTemplates = async () => {
            try {
                const response = await apiService.get(
                    `/notification/email-template?project_key=${encodeURIComponent(projectKey)}`
                );
                const rows: ApiTemplateRow[] = response?.data?.data?.email_templates ?? [];
                const map: Record<string, boolean> = {};
                rows.forEach((r) => {
                    if (r.type && r.is_custom) map[r.type] = true;
                });
                setCustomized(map);
            } catch (error: any) {
                apiService.showSnack(
                    error?.response?.data?.message || "Failed to load templates",
                    "error"
                );
            }
        };
        if (projectKey) fetchTemplates();
    }, [projectKey]);

    const openEditor = (type: TemplateType) =>
        navigate(`/application-settings/email-config/${projectKey}/templates/${type}`, {
            state: { projectName },
        });

    return (
        <Box>
            {/* Breadcrumb / header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <IconButton size="small" onClick={() => navigate("/application-settings/messaging/email")} data-qa="btn-back-configs">
                    <FontAwesomeIcon icon={faArrowLeft} />
                </IconButton>
                <Typography variant="body2" color="text.secondary">
                    <Link
                        component="button"
                        underline="hover"
                        color="inherit"
                        onClick={() => navigate("/application-settings/messaging/email")}
                        sx={{ verticalAlign: "baseline" }}
                    >
                        Email configuration
                    </Link>
                    {"  ›  "}
                    <Box component="span" sx={{ color: "text.primary", fontWeight: 600 }}>
                        {projectName}
                    </Box>
                </Typography>
            </Box>

            <Typography variant="h6" fontWeight={600} sx={{ ml: 5.5, mb: 0.5 }}>
                Email templates
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 5.5, mb: 3, maxWidth: 720 }}>
                These templates are used when this project sends email. Each starts from a
                platform default — edit any of them to give <b>{projectName}</b> its own wording, and
                reset back to the default at any time.
            </Typography>

            <Grid container spacing={2} sx={{ ml: 0 }}>
                {TYPE_LIST.map((t) => {
                    const isCustom = !!customized[t.key];
                    return (
                        <Grid size={{ xs: 12, sm: 6, md: 4 }} key={t.key}>
                            <Card variant="outlined" sx={{ height: "100%", borderRadius: 2 }}>
                                <CardActionArea
                                    onClick={() => openEditor(t.key)}
                                    sx={{ p: 2.5, height: "100%", display: "block" }}
                                    data-qa={`template-type-${t.key}`}
                                >
                                    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", mb: 1 }}>
                                        <Box
                                            sx={{
                                                width: 40,
                                                height: 40,
                                                borderRadius: 1.5,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: "action.hover",
                                                color: "primary.main",
                                            }}
                                        >
                                            {typeIcon[t.key] ?? <MailOutline />}
                                        </Box>
                                        <Chip
                                            size="small"
                                            label={isCustom ? "Customized" : "Using default"}
                                            color={isCustom ? "primary" : "default"}
                                            variant={isCustom ? "filled" : "outlined"}
                                            sx={{ fontWeight: 600 }}
                                        />
                                    </Box>
                                    <Typography variant="subtitle1" fontWeight={600}>
                                        {t.label}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, minHeight: 40 }}>
                                        {t.description}
                                    </Typography>
                                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                        {t.hasActions ? (
                                            <Chip size="small" variant="outlined" label="Has action buttons" />
                                        ) : (
                                            <span />
                                        )}
                                        <Box sx={{ display: "flex", alignItems: "center", color: "primary.main", fontWeight: 600, fontSize: 14 }}>
                                            Edit <ChevronRight fontSize="small" />
                                        </Box>
                                    </Box>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    );
                })}
            </Grid>
        </Box>
    );
}

export default usePageWrapper(ProjectTemplates, {
    page_title: "Email Templates",
});
